import endpoints from '@/libs/endpoints'
import { useCognito } from '@/views/habit/awsCognito'

const { getRequestHeaders } = useCognito()

/**
 * Plugin - MongoDB Operations via AWS Lambda
 * Replaces Realm SDK with Lambda invocations authenticated by Cognito
 */

// Configuration
//
// Los tres valores eran obligatorios pero tenían un respaldo hardcodeado, y los tres respaldos
// eran peligrosos justamente durante la migración a una cuenta por app:
//
//   apiURL    apuntaba al API Gateway compartido de la cuenta vieja. Si la variable no llegaba
//             al build, la app seguía funcionando contra la cuenta de otro cliente sin que nada
//             lo dijera: tráfico cruzado y costo facturado a quien no corresponde.
//   database  'development'. Hoy ese valor es además la clave con la que la Lambda elige el
//             bucket, así que producción habría escrito en el bucket de desarrollo.
//   appName   'habit'. Es lo que identifica a la app del otro lado.
//
// Ahora falta una y la app no arranca. Es a propósito y es lo que pide la Fase 1: una falla al
// arrancar se ve en el primer minuto; un respaldo silencioso se descubre en la factura.
const configuracionFaltante = Object.entries({
  VUE_APP_API_URL: process.env.VUE_APP_API_URL,
  VUE_APP_APP_NAME: process.env.VUE_APP_APP_NAME,
  // No se lee de process.env directo porque endpoints.js elige la variable según el entorno:
  // en producción es VUE_APP_DATABASE_NAME_PRODUCTION.
  'VUE_APP_DATABASE_NAME_* (endpoints.database_name)': endpoints.database_name,
})
  .filter(([, valor]) => !valor)
  .map(([nombre]) => nombre)

if (configuracionFaltante.length) {
  throw new Error(
    `Faltan variables de entorno y no hay valor por defecto a propósito: ${configuracionFaltante.join(', ')}. `
    + 'Sin ellas la app no sabe contra qué cuenta de AWS está hablando.',
  )
}

const LAMBDA_CONFIG = {
  database: endpoints.database_name,
  appName: process.env.VUE_APP_APP_NAME,
  apiURL: process.env.VUE_APP_API_URL,
}

/**
 * Invoke Lambda function with authentication and payload
 * @param operation - MongoDB operation name
 * @param params - Operation parameters
 * @returns Lambda response data
 */
async function invokeLambda(operation, params) {
  try {
    // 1. Get request headers (auth token or guest ID)
    const headers = await getRequestHeaders()
    if (!headers) {
      throw new Error('Not authenticated - no Cognito token found')
    }

    // 2. Set path based on headers
    const path = headers['Authorization'] ? '/db' : '/guest/db'

    // 3. Build Lambda payload
    const payload = {
      operation,
      database: LAMBDA_CONFIG.database,
      appName: LAMBDA_CONFIG.appName,
      ...params
    }

    // 4. Invoke Lambda with API Gateway
    const response = await fetch(`${LAMBDA_CONFIG.apiURL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify(payload)
    })

    const result = await response.json()

    // Success response
    if (result.success) {
        // If data is from S3, fetch the actual data from the provided URL
        if (result.data?.fromS3) {
          const s3Data = await fetch(result.data.url).then(res => res.json())
          return s3Data
        }

        return result.data
    }

    // Error response
    return result
  } catch (error) {
    console.log(error)
    throw error
  }
}

// =============================
// Composable
// =============================

export function useLambda() {
  /**
   * Get single item from collection
   * @param params - Collection, query, and options
   * @returns Single document or null
   */
  const getItem = async ({ collection, query, options = {} }) => {
      const result = await invokeLambda('getItem', { collection, query, options })
      return result
  }

  /**
   * Get multiple items from collection
   * @param params - Collection, query, and options
   * @returns Array of documents
   */
  const getItems = async ({ collection, query, options = {} }) => {
      const result = await invokeLambda('getItems', { collection, query, options })
      return result || []
  }

  /**
   * Get items with custom aggregate pipeline
   */
  const getItemsAggregate = async ({ collection, pipeline }) => {
      const result = await invokeLambda('getItemsAggregate', { collection, pipeline })
      return result || []
  }

  /**
   * Get and update items in collection (findOneAndUpdate)
   * @param params - Collection, query, action and options
   * @returns Single document or null
   */
  const getItemAndUpdate = async ({ collection, query, action, options }) => {
      const result = await invokeLambda('getItemAndUpdate', { collection, query, action, options })
      return result
  }

  /**
   * Create single item in collection
   */
  const createItem = async ({ collection, payload }) => {
      const result = await invokeLambda('createItem', { collection, payload })
      return result
  }

  /**
   * Create multiple items in collection
   */
  const createItems = async ({ collection, payload }) => {
      const result = await invokeLambda('createItems', { collection, payload })
      return result
  }

  /**
   * Update single item in collection
   */
  const updateItem = async ({ collection, query, action }) => {
      const result = await invokeLambda('updateItem', { collection, query, action })
      return result
  }

  /**
   * Update multiple items in collection
   */
  const updateItems = async ({ collection, query, action }) => {
      const result = await invokeLambda('updateItems', { collection, query, action })
      return result
  }

  /**
   * Delete single item from collection
   */
  const deleteItem = async ({ collection, query }) => {
      const result = await invokeLambda('deleteItem', { collection, query })
      return result
  }

  /**
   * Delete multiple items from collection
   */
  const deleteItems = async ({ collection, query }) => {
      const result = await invokeLambda('deleteItems', { collection, query })
      return result
  }

  /**
   * Send email
   */
  // Sin `from`: hay un remitente verificado por cuenta y lo resuelve la Lambda a partir de su
  // propia configuración. Mandarlo desde acá no agregaba nada — la política del rol ya fijaba
  // una única dirección permitida — y agregaba una forma de fallar.
  const sendEmail = async ({ to, subject, htmlBody, attachment }) => {
      const result = await invokeLambda('sendEmail', { to, subject, htmlBody, attachment })
      return result
  }

  /**
   * Get presigned upload url
   */
  // Sin `bucket`: la Lambda lo resuelve con el `database` que ya viaja en cada payload. Es el
  // mismo motivo que en sendEmail — el cliente elegía el nombre de un recurso de la cuenta.
  const getPresignedUploadUrl = async ({ fileName, fileType, destinationFolder }) => {
      const result = await invokeLambda('getPresignedUploadUrl', { fileName, fileType, destinationFolder })
      return result
  }

  /**
   * Get presigned download url
   */
  const getPresignedDownloadUrl = async ({ key }) => {

      const result = await invokeLambda('getPresignedDownloadUrl', { key })
      return result
  }

  /**
   * Create Cognito user
   */
  const createCognitoUser = async ({ email, name, password }) => {
      // La Lambda crea el usuario en Cognito y el pool es case-sensitive: el email va
      // normalizado a minúsculas para que el login posterior (que también normaliza)
      // matchee.
      const result = await invokeLambda('createCognitoUser', { email: email.trim().toLowerCase(), name, password })
      return result
  }

  /**
   * Change Cognito password
   */
  const changeCognitoPassword = async ({ accessToken, oldPassword, newPassword }) => {
      const result = await invokeLambda('changeCognitoPassword', { accessToken, oldPassword, newPassword })
      return result
  }

  /**
   * Update Cognito user email (admin operation)
   */
  const updateCognitoEmail = async ({ currentEmail, newEmail }) => {
      const result = await invokeLambda('updateCognitoEmail', { currentEmail, newEmail })
      return result
  }

  return {
      getItem,
      getItems,
      getItemsAggregate,
      getItemAndUpdate,
      createItem,
      createItems,
      updateItem,
      updateItems,
      deleteItem,
      deleteItems,
      sendEmail,
      getPresignedUploadUrl,
      getPresignedDownloadUrl,
      createCognitoUser,
      changeCognitoPassword,
      updateCognitoEmail,
  }
}